import type { Usuario } from '../api';

/**
 * Las dos reglas que el backend rechaza con `409 operacion_invalida` sobre la
 * fila del propio usuario (KAN-143).
 *
 * # POR QUÉ ESTÁN ACÁ Y NO SOLO EN EL BACKEND
 *
 * **No son una validación del cliente.** El backend las aplica igual y es él el
 * que manda; esto es lo que hace que la pantalla no ofrezca un botón cuyo único
 * destino posible es un error. Un botón habilitado que siempre falla es peor que
 * no tenerlo: enseña a apretar y ver qué pasa.
 *
 * Y como no son una validación, **el `409` se sigue manejando**: la lista que
 * tiene la pantalla puede estar vieja (otro administrador cambió algo hace un
 * segundo), y en ese caso el botón habilitado es correcto y la respuesta también.
 *
 * # LAS DOS REGLAS
 *
 * 1. **Nadie se da de baja a sí mismo.** Quedarse afuera del sistema por un clic
 *    no se revierte solo: haría falta otro administrador.
 * 2. **Un administrador no se quita el rol a sí mismo.** Si el último admin se
 *    degrada, no queda nadie que pueda promoverlo y hay que ir al bootstrap por
 *    línea de comandos, que solo corre si NO hay ningún admin activo. O sea: el
 *    sistema quedaría sin administración hasta que alguien entre al servidor.
 *
 * Las dos devuelven **el motivo, no un booleano**, porque la tarjeta pide que el
 * motivo esté a la vista. Un `puedeX(): boolean` obligaría a escribir el texto en
 * otro lado y a mantener los dos sincronizados.
 */

/** El motivo por el que NO se puede desactivar, o `null` si se puede. */
export function motivoParaNoDesactivar(usuario: Usuario, yoId: number): string | null {
  if (usuario.id === yoId) {
    return 'No podés darte de baja a vos mismo: quedarías afuera y haría falta otro administrador para volver a entrar.';
  }
  return null;
}

/** El motivo por el que NO se puede cambiar el rol, o `null` si se puede. */
export function motivoParaNoCambiarRol(usuario: Usuario, yoId: number): string | null {
  if (usuario.id === yoId) {
    // Esta pantalla solo la ve un ADMIN, así que cualquier cambio de rol sobre
    // la fila propia es una degradación. Por eso el control se bloquea entero y
    // no solo la opción OPERADOR.
    return 'No podés quitarte el rol de administrador a vos mismo: si fueras el último, no quedaría nadie para volver a promoverte.';
  }
  return null;
}
